const columns = (
  statusFilters = [],
  paymentMethodsFilters = [],
  isRushFilters = [],
  filteredData = [],
  filteredInfo = {},
) => {
  const paymentMethodColumns = [];
  const methodsSet = new Set();

  filteredData.forEach((record) => {
    if (record.pay?.methods && Array.isArray(record.pay.methods)) {
      record.pay.methods.forEach((method) => {
        if (method) methodsSet.add(method);
      });
    }
  });

  methodsSet.forEach((method) => {
    paymentMethodColumns.push({
      title: method,
      key: `payment_${method}`,
      sorter: (a, b) => {
        const aPayments = a.pay?.history?.filter((h) => h.payment_method === method) || [];
        const bPayments = b.pay?.history?.filter((h) => h.payment_method === method) || [];
        const aAmount = aPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        const bAmount = bPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        return aAmount - bAmount;
      },
      customRender: ({ record }) => {
        const payments = record.pay?.history?.filter((h) => h.payment_method === method) || [];
        if (payments.length === 0) return '-';
        const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
        return `$${Number(totalAmount).toFixed(2)}`;
      },
    });
  });

  return [
    {
      title: 'Order ID',
      dataIndex: ['order', 'id'],
      key: 'order.id',
      sorter: (a, b) => a.order.id - b.order.id,
      customFilterDropdown: true,
      onFilter: (value, record) => record.order.id.toString().includes(value),
      filteredValue: filteredInfo['order.id'] || null,
    },
    {
      title: 'Date',
      dataIndex: ['order', 'addedAt'],
      key: 'order.addedAt',
      sorter: (a, b) => new Date(a.order.addedAt) - new Date(b.order.addedAt),
      defaultSortOrder: 'descend',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        if (!value || !Array.isArray(value) || value.length !== 2) return true;
        const recordDate = new Date(record.order.addedAt);
        const startDate = new Date(value[0]);
        const endDate = new Date(value[1]);
        return recordDate >= startDate && recordDate <= endDate;
      },
      filteredValue: filteredInfo['order.addedAt'] || null,
    },
    {
      title: 'Customer First Name',
      dataIndex: ['costumer', 'firstName'],
      key: 'costumer.firstName',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const firstName = record.costumer?.firstName || '';
        return firstName.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo['costumer.firstName'] || null,
    },
    {
      title: 'Customer Last Name',
      dataIndex: ['costumer', 'lastName'],
      key: 'costumer.lastName',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const lastName = record.costumer?.lastName || '';
        return lastName.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo['costumer.lastName'] || null,
    },
    {
      title: 'Customer Email',
      dataIndex: ['costumer', 'email'],
      key: 'costumer.email',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const email = record.costumer?.email || '';
        return email.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo['costumer.email'] || null,
    },
    {
      title: 'Concierge',
      dataIndex: 'concierge',
      key: 'concierge',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const { concierge } = record;
        if (!concierge || !concierge.firstName) {
          return 'website'.toLowerCase().includes(value.toLowerCase());
        }
        const fullName = `${concierge.firstName || ''} ${concierge.lastName || ''}`.trim();
        return fullName.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo.concierge || null,
    },
    {
      title: 'Status',
      dataIndex: ['status', 'name'],
      key: 'status.name',
      filters: statusFilters,
      onFilter: (value, record) => record.status?.name === value,
      filteredValue: filteredInfo['status.name'] || null,
    },
    {
      title: 'Promo Code',
      dataIndex: ['promoCode', 'name'],
      key: 'promoCode.name',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const promoName = record.promoCode?.name || '';
        return promoName.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo['promoCode.name'] || null,
    },
    {
      title: 'Is Rush',
      dataIndex: ['order', 'isQuickly'],
      key: 'order.isQuickly',
      filters: isRushFilters,
      onFilter: (value, record) => record.order?.isQuickly === value,
      filteredValue: filteredInfo['order.isQuickly'] || null,
    },
    {
      title: 'Billing Address',
      dataIndex: 'billing',
      key: 'billing',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const { billing } = record;
        if (!billing) return false;
        const fullAddress = `${billing.streetAddress || ''} ${billing.city || ''} ${billing.state || ''} ${billing.zipCode || ''}`.trim();
        return fullAddress.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo.billing || null,
    },
    {
      title: 'Shipping Address',
      dataIndex: 'shipping',
      key: 'shipping',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const { shipping } = record;
        if (!shipping) return false;
        const fullAddress = `${shipping.streetAddress || ''} ${shipping.city || ''} ${shipping.state || ''} ${shipping.zipCode || ''}`.trim();
        return fullAddress.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo.shipping || null,
    },
    {
      title: 'Creator',
      dataIndex: 'creator',
      key: 'creator',
      customFilterDropdown: true,
      onFilter: (value, record) => {
        const { creator } = record;
        if (!creator || !creator.firstName) {
          return 'website'.toLowerCase().includes(value.toLowerCase());
        }
        const fullName = `${creator.firstName || ''} ${creator.lastName || ''}`.trim();
        return fullName.toLowerCase().includes(value.toLowerCase());
      },
      filteredValue: filteredInfo.creator || null,
    },
    {
      title: 'Price',
      dataIndex: ['order', 'price'],
      key: 'order.price',
      sorter: (a, b) => (a.order?.price || 0) - (b.order?.price || 0),
    },
    {
      title: 'Total Tax Amount',
      dataIndex: ['order', 'taxAmount'],
      key: 'order.taxAmount',
      sorter: (a, b) => (a.order?.taxAmount || 0) - (b.order?.taxAmount || 0),
    },
    {
      title: 'Pay Tax Amount',
      dataIndex: ['pay', 'taxAmount'],
      key: 'pay.taxAmount',
      sorter: (a, b) => (a.pay?.taxAmount || 0) - (b.pay?.taxAmount || 0),
    },
    {
      title: 'Discount Amount',
      dataIndex: ['order', 'discountAmount'],
      key: 'order.discountAmount',
      sorter: (a, b) => (a.order?.discountAmount || 0) - (b.order?.discountAmount || 0),
    },
    {
      title: 'Total Payed',
      dataIndex: ['pay', 'payed'],
      key: 'pay.payed',
      sorter: (a, b) => (a.pay?.payed || 0) - (b.pay?.payed || 0),
    },
    {
      title: 'Balance Due',
      dataIndex: ['pay', 'needToPay'],
      key: 'pay.needToPay',
      sorter: (a, b) => (a.pay?.needToPay || 0) - (b.pay?.needToPay || 0),
    },
    {
      title: 'Payment Methods',
      dataIndex: ['pay', 'methods'],
      key: 'pay.methods',
      filters: paymentMethodsFilters,
      onFilter: (value, record) => {
        if (!record.pay?.methods || !Array.isArray(record.pay.methods)) return false;
        return record.pay.methods.includes(value);
      },
      filteredValue: filteredInfo['pay.methods'] || null,
    },
    ...paymentMethodColumns,
  ];
};

export default columns;
